/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { openDialog } from '../../../app/dialog/dialog.actions';
import { initialHomePage } from '../../../app/page/home/home.actions';
import { pageStateSelectors } from '../../../app/page/page-state/page-state.selectors';
import { useSelect, useSelectWithParams } from '../../../utils/selector.utils';
import { TaskFormTrigger } from '../../dialogs/TaskFormDialog/TaskFormTrigger';
import { PageLayout } from '../common/PageLayout';
import { WidgetWrapperSingleColumn } from '../common/widgets/WidgetWrapperSingleColumn';
import { UserBar } from './UserBar';
import { authSelectors } from '../../../app/auth/auth.selectors';
import { TaskDragDropContext } from '../common/drag-drop-contexts/TaskDragDropContext';
import { CalendarWidgetList } from '../common/widget-lists/CalendarWidgetList/intedx';
import { calendarsPageSelectors } from '../../../app/page/page-storage/calendars/calendars.selectors';
import { tasksSimplePageSelectors } from '../../../app/page/page-storage/tasks-simple/tasks-simple.selectors';
import { ITask } from '../../../app/resource/task/task.types';
import { useParams } from 'react-router-dom';
import { taskSimpleSelectors } from '../../../app/resource/task-simple/task-simple.selectors';
import { setupSharedTask } from '../../../app/page/page-storage/tasks-simple/tasks-simple.actions';
import { setupActionItems } from '../../../app/page/page-storage/meeting-tasks/meeting-tasks.saga-services';
import { OpenCalendarFormButton } from './OpenCalendarFormButton';
import { createPersonalNote } from '../../../app/page/page-storage/personal-notes/personal-notes.actions';
import { WidgetSortableTasksList } from '../common/widgets/WidgetSortableTasksList';
import { InfoIconPopup } from '../common/InfoIconPopup';
import { userSettingsPageSelectors } from '../../../app/page/page-storage/user-settings/user-settings.selectors';
import { WidgetKpiMeetingTab } from '../common/widgets/widgets-kpi/WidgetKpiMeetingTabWrapper';
import { WidgetSizeDropdown } from '../common/widgets/WidgetSizeDropdown';
import { updateUserWidgetSize } from '../../../app/page/page-storage/user-settings/user-settings.actions';
import { PersonalNotesWidget } from '../common/widgets/PersonalNotesWidget';
import { WidgetPersonalNotesListCollapse } from '../common/widgets/WidgetPersonalNotesListCollapse';
import { CirclePlusButton } from '../common/CirclePlusButton';

export const HomePage: FunctionComponent = () => {
  const showLoader = useSelectWithParams(pageStateSelectors.selectPageInProgress, 'home');
  const dispatch = useDispatch();
  const [isPersonalNotesListCollapsed, setPersonalNotesListCollapsed] = useState(false);

  let widgets = useSelect(userSettingsPageSelectors.selectUserWidgets) ?? [];
  widgets = widgets.filter((widget) => widget.visible);

  const WIDGET_SIZE_OPTIONS: { [key: string]: string[] } = {
    homePersonalNotes: ['1/3', '1/2', '2/3', 'full'],
    homeCalendar: ['1/3', '1/2', '2/3', 'full'],
    homeTodos: ['1/3', '1/2', '2/3', 'full'],
    homeQis: ['1/3', '1/2', '2/3', 'full'],
  };

  const currentUserId = useSelect(authSelectors.selectLoggedUser);

  const tasks = useSelect(taskSimpleSelectors.selectResourceList);
  const qiTasks = useSelect(tasksSimplePageSelectors.selectQis);

  const allActionItems = setupActionItems(tasks);
  const myActionItems = allActionItems[currentUserId ? currentUserId : ''];

  const calendarItemIds = useSelect(calendarsPageSelectors.selectCalendarItems);

  const openTaskForm = useCallback(() => {
    dispatch(openDialog('dbTaskFormDialog'));
  }, [dispatch]);

  const createNote = useCallback(() => {
    dispatch(createPersonalNote());
  }, [dispatch]);

  useEffect(() => {
    dispatch(initialHomePage());
  }, [dispatch]);

  const { taskId } = useParams<{ taskId: ITask['id'] }>();
  const task = useSelectWithParams(taskSimpleSelectors.selectResourceById, taskId);

  useEffect(() => {
    if (!task && taskId) {
      dispatch(setupSharedTask(taskId));
    }
    if (task) {
      dispatch(openDialog('trelloReplacementFormDialog', { taskId, meetingId: '' }));
    }
  }, [dispatch, task, taskId]);

  return (
    <PageLayout showLoader={showLoader} aditionalNavbar={<UserBar />} showHide={true}>
      {!showLoader && (
        <div className="row mb-25 home-page-class">
          {widgets.map((widget, index) => {
            if (widget.key === 'homeKpis') {
              return <WidgetKpiMeetingTab key={index} />;
            } else if (widget.key === 'homeCalendar') {
              return (
                <WidgetWrapperSingleColumn
                  size={widget.size}
                  name={
                    <>
                      Calendar{' '}
                      <InfoIconPopup>
                        In order to embed your Google calendar you need to enter your Google workspace/Gmail email
                        address. You can also enter the Google calendar embed URL directly, which you can obtain from
                        the Google calendar settings page.
                      </InfoIconPopup>
                    </>
                  }
                  key={index}
                  headerElements={[
                    <WidgetSizeDropdown
                      key="size"
                      options={WIDGET_SIZE_OPTIONS[widget.key]}
                      value={widget.size}
                      onChange={(newSize) => {
                        if (newSize !== widget.size) {
                          dispatch(updateUserWidgetSize(widget.key, newSize));
                        }
                      }}
                    />,
                  ]}
                  actionElement={<OpenCalendarFormButton />}
                >
                  <CalendarWidgetList calendarItemId={calendarItemIds[0]} />
                </WidgetWrapperSingleColumn>
              );
            } else if (widget.key === 'homeTodos' && myActionItems) {
              return (
                <TaskDragDropContext key={index}>
                  <WidgetWrapperSingleColumn
                    key={index}
                    size={widget.size}
                    name="My Action Items"
                    headerElements={[
                      <WidgetSizeDropdown
                        key="size"
                        options={WIDGET_SIZE_OPTIONS[widget.key]}
                        value={widget.size}
                        onChange={(newSize) => {
                          if (newSize !== widget.size) {
                            dispatch(updateUserWidgetSize(widget.key, newSize));
                          }
                        }}
                      />,
                    ]}
                    filterElement={
                      <label htmlFor="" className="f-1 flex aic mb-2">
                        show/hide:
                        <a className="tab-button" data-target="company-todo">
                          Company To Do’s
                        </a>
                        <a className="selected tab-button" data-target="account-todo">
                          Account To Do’s
                        </a>
                      </label>
                    }
                    actionElement={
                      <a
                        className="btn-badge theme-color pointer popup_button"
                        data-popup="new_task_popup"
                        title="Create Card"
                        onClick={openTaskForm}
                      >
                        <CirclePlusButton title="Create Card" size={25} onClick={openTaskForm} />
                      </a>
                    }
                  >
                    <WidgetSortableTasksList
                      activeIds={myActionItems.active}
                      doneIds={myActionItems.done}
                      type={'board'}
                    />
                  </WidgetWrapperSingleColumn>
                </TaskDragDropContext>
              );
            } else if (widget.key === 'homeQis') {
              return (
                <TaskDragDropContext key={index}>
                  <WidgetWrapperSingleColumn
                    key={index}
                    size={widget.size}
                    name="Goals"
                    headerElements={[
                      <WidgetSizeDropdown
                        key="size"
                        options={WIDGET_SIZE_OPTIONS[widget.key]}
                        value={widget.size}
                        onChange={(newSize) => {
                          if (newSize !== widget.size) {
                            dispatch(updateUserWidgetSize(widget.key, newSize));
                          }
                        }}
                      />,
                    ]}
                    actionElement={<TaskFormTrigger />}
                    filterElement={
                      <label htmlFor="" className="f-1 flex aic mb-2">
                        show/hide:
                        <a className="tab-button" data-target="company-todo">
                          Company Goal`s
                        </a>
                        <a className="selected tab-button" data-target="account-todo">
                          Account Goal`s
                        </a>
                      </label>
                    }
                  >
                    <WidgetSortableTasksList activeIds={qiTasks.active} doneIds={qiTasks.done} type={'qi'} />
                  </WidgetWrapperSingleColumn>
                </TaskDragDropContext>
              );
            } else if (widget.key === 'homePersonalNotes') {
              return (
                <WidgetWrapperSingleColumn
                  key={index}
                  size={widget.size}
                  name="Personal Notes"
                  headerElements={[
                    <WidgetPersonalNotesListCollapse
                      key="collapse"
                      collapsed={isPersonalNotesListCollapsed}
                      onClick={() => setPersonalNotesListCollapsed((c) => !c)}
                    />,
                    <WidgetSizeDropdown
                      key="size"
                      options={WIDGET_SIZE_OPTIONS[widget.key]}
                      value={widget.size}
                      onChange={(newSize) => {
                        if (newSize !== widget.size) {
                          dispatch(updateUserWidgetSize(widget.key, newSize));
                        }
                      }}
                    />,
                  ]}
                  actionElement={
                    <a
                      className="btn-badge theme-color pointer popup_button"
                      title="Add Note"
                      onClick={createNote}
                      href="#"
                    >
                      <CirclePlusButton title="Add Note" size={25} onClick={createNote} />
                    </a>
                  }
                >
                  <PersonalNotesWidget isNotesListCollapsed={isPersonalNotesListCollapsed} />
                </WidgetWrapperSingleColumn>
              );
            } else {
              return null;
            }
          })}
        </div>
      )}
    </PageLayout>
  );
};
